import {
  put, call, delay, select,
} from 'lib/redux';
import { logError } from 'utils/logger.utils';
import propertyDataRequestService from 'services/propertyDataRequest.service';
import propertyDataResultService from 'services/propertyDataResult.service';
import propertyDataImageMetadataRequestService from 'services/propertyDataImageMetadata.service';
import {
  propertyDataRequestSuccess,
  propertyDataRequestError,
  propertyDataResultSuccess,
  propertyDataResultError,
  propertyDataRequestLoading as propertyDataRequestLoadingAction,
  propertyDataResultLoading as propertyDataResultLoadingAction,
} from './Order.actions';

export function* fetchPropertyDataImagesSaga({ latitude, longitude } = {}) {
  try {
    if (!latitude || !longitude) {
      throw new Error('Latitude and longitude are required');
    }

    const imageMetadataResponse = yield call(propertyDataImageMetadataRequestService, { latitude, longitude });

    if (!imageMetadataResponse.ok) {
      throw new Error('Failed to get property data images');
    }

    const captures = imageMetadataResponse.json?.captures || [];

    if (!Array.isArray(captures) || captures.length === 0) return {};
    const { urn, shot_time: shotTime } = captures[0]?.orthos?.images?.[0] || {};

    return { urn, shotTime };
  } catch (error) {
    logError({
      message: 'Error in fetchPropertyDataImagesSaga',
      error: error.message,
    });
    yield put(propertyDataRequestError(error.message));
    return null;
  }
}

/**
 * Saga to handle property data result
 */
export function* propertyDataResultSaga({ requestId, orgNodeId }, maxAttempts = 12) {
  try {
    yield put(propertyDataResultLoadingAction());
    const { latitude, longitude } = yield select(({ orderAddresses }) => orderAddresses.geocodedAddress);

    // Call the property data result API
    const response = yield call(propertyDataResultService, requestId, orgNodeId);

    if (!response.ok) {
      throw new Error(response.json?.errorDescription || 'Failed to get property data result');
    }

    if (response.status === 202) {
      if (maxAttempts > 0) {
        // Wait for 10 seconds and try again
        yield delay(10000);
        yield call(propertyDataResultSaga, { requestId, orgNodeId }, maxAttempts - 1);
      } else {
        throw new Error('Property data result not ready after multiple attempts');
      }
    } else {
      const imagery = response.json?.imagery;
      // #genai Safely destructure result, defaulting to empty object if null/undefined
      const fetchImageResult = yield call(fetchPropertyDataImagesSaga, { latitude, longitude });
      const { urn, shotTime } = fetchImageResult || {};
      const latestImageDate = Object.entries(imagery || {}).reduce((acc, [, imageData]) => {
        const shotDate = new Date(imageData?.metadata?.shot_date);
        if (!acc || shotDate > acc) {
          return shotDate;
        }
        return acc;
      }, null);

      // #genai isEVImageLatest should be false when captures is empty (urn and shotTime will be undefined)
      const isEVImageLatest = shotTime && urn && latestImageDate && new Date(shotTime) > latestImageDate;

      yield put(propertyDataResultSuccess(
        response.json,
        isEVImageLatest && {
          urn,
          shotTime,
          longitude,
          latitude,
        },
      ));
    }
  } catch (error) {
    logError({
      message: 'Error in propertyDataResultSaga',
      error: error.message,
    });
    yield put(propertyDataResultError(error.message));
  }
}

/**
 * Saga to handle property data request
 */
export function* propertyDataRequestSaga(action) {
  try {
    yield put(propertyDataRequestLoadingAction());
    const { completeAddress } = action;

    // Call the property data request API
    const response = yield call(propertyDataRequestService, completeAddress);

    if (response.ok && response.json) {
      const { request } = response.json;
      yield put(propertyDataRequestSuccess(request));
      yield put(propertyDataResultLoadingAction());

      // Then call the result API with the request ID
      const orgNodeId = yield select(({ userData }) => userData.initParameters.orgNodeId);
      yield propertyDataResultSaga({ requestId: request.id, orgNodeId });
    } else {
      const error = response.json?.message || 'Failed to request property data';
      yield put(propertyDataRequestError(error));
    }
  } catch (error) {
    logError({
      message: 'Error in propertyDataRequestSaga',
      error: error.message,
    });
    yield put(propertyDataRequestError(error.message));
  }
}
